import React from 'react';
import { XMarkIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { LinkedAdherent } from '../../store/slices/nouveauxAdherentsSlice';

interface LinkedAdherentsPopupProps {
  adherents: LinkedAdherent[];
  onClose: () => void;
}

const LinkedAdherentsPopup: React.FC<LinkedAdherentsPopupProps> = ({ adherents, onClose }) => {
  if (!adherents || adherents.length === 0) return null;
  
  const handleSelect = (id: number) => {
    // Open adherent details in a new tab
    window.open(`/adherents/${id}`, '_blank');
    onClose();
  };
  
  // Helper function to format address
  const formatAddress = (adherent: LinkedAdherent) => {
    if (!adherent.address) return { street: '', location: '' };
    
    const { numeroVoie, typeVoie, libelleVoie, postalCode, city } = adherent.address;
    
    const streetParts = [];
    if (numeroVoie) streetParts.push(numeroVoie);
    if (typeVoie) streetParts.push(typeVoie);
    if (libelleVoie) streetParts.push(libelleVoie);
    
    const street = streetParts.join(' ');
    const location = [postalCode, city].filter(Boolean).join(' ');
    
    return { street, location };
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">
            {adherents.length > 1 
              ? `${adherents.length} adhérents liés`
              : '1 adhérent lié'}
          </h3>
          <button 
            onClick={onClose}
            className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
        
        <p className="text-sm text-gray-600 mb-4">
          Ces établissements appartiennent à la même société. Sélectionnez celui dont vous souhaitez voir les détails :
        </p>
        
        <div className="space-y-2 max-h-60 overflow-y-auto">
          {adherents.map((adherent) => (
            <div 
              key={adherent.id} 
              className="border border-gray-200 rounded-md p-3 hover:bg-blue-50 transition cursor-pointer"
              onClick={() => handleSelect(adherent.id)}
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="font-medium text-gray-900">{adherent.name}</div>
                  {(formatAddress(adherent).street || formatAddress(adherent).location) && (
                    <div className="text-xs text-gray-500 mt-1">
                      {formatAddress(adherent).street}
                      {formatAddress(adherent).street && formatAddress(adherent).location && <br />}
                      {formatAddress(adherent).location}
                    </div>
                  )}
                </div>
                <ArrowTopRightOnSquareIcon className="h-4 w-4 text-blue-500 flex-shrink-0 ml-2" />
              </div>
            </div>
          ))}
        </div>
        
        <div className="mt-5 flex justify-end space-x-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-transparent rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};

export default LinkedAdherentsPopup; 